import type { TFunction } from 'i18next'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import i18n from 'src/i18n/i18next'

type TranslateErrorCodeOptions = {
  fallback?: string
  /**
   * When provided, we use the caller's translator instead of the global i18n instance.
   * Mostly useful for the React hook variant to keep reactivity with locale switches.
   */
  t?: TFunction<'error'>
}

const ERROR_NAMESPACE = 'error'
const ERROR_CODES_PREFIX = 'codes'
const FALLBACK_KEY = 'fallback'

const getDefaultFallback = (t?: TFunction<'error'>) => {
  const translator = t ?? (i18n.t.bind(i18n) as TFunction<'error'>)
  return translator(`${ERROR_NAMESPACE}:${FALLBACK_KEY}`, {
    defaultValue: 'Something went wrong. Please try again.',
  })
}

function normalizeCode(rawCode: string) {
  const stripped = (rawCode || '').trim()
  if (!stripped) return ''
  return (
    stripped
      // Remove quotes and backticks
      .replace(/['"`]/g, '')
      // Convert camelCase or PascalCase to kebab-case
      .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
      // Replace spaces, underscores, and periods with hyphens
      .replace(/[\s_.]+/g, '-')
      // Replace any non-alphanumeric, non-hyphen character with hyphen
      .replace(/[^a-zA-Z0-9-]+/g, '-')
      // Collapse multiple hyphens into one
      .replace(/-+/g, '-')
      // Remove leading and trailing hyphens
      .replace(/^-+|-+$/g, '')
      .toLowerCase()
  )
}

export const translateOnerwayErrorCode = (
  code?: string | null,
  opt: TranslateErrorCodeOptions = {},
) => {
  const { fallback, t } = opt
  const translator = t ?? (i18n.t.bind(i18n) as TFunction<'error'>)
  if (!code) {
    return fallback ?? getDefaultFallback(translator)
  }
  const key = `${ERROR_NAMESPACE}:onerway.${code}`
  if (i18n.exists(key)) {
    const translated = translator(key)
    if (translated) return translated
  }
  return fallback ?? getDefaultFallback(translator)
}

export const translateErrorCode = (
  code?: string | null,
  opt: TranslateErrorCodeOptions = {},
) => {
  const { fallback, t } = opt
  const translator = t ?? (i18n.t.bind(i18n) as TFunction<'error'>)
  if (!code) {
    return fallback ?? getDefaultFallback(translator)
  }
  const normalizedCode = normalizeCode(code)
  const key = `${ERROR_NAMESPACE}:${ERROR_CODES_PREFIX}.${normalizedCode}`
  if (i18n.exists(key)) {
    const translated = translator(key)
    if (translated) return translated
  }
  return fallback ?? getDefaultFallback(translator)
}

type HookTranslateErrorCodeOptions = Omit<TranslateErrorCodeOptions, 't'>

export const useErrorCodeTranslator = () => {
  const { t } = useTranslation(ERROR_NAMESPACE)
  return useCallback(
    (code?: string | null, opt: HookTranslateErrorCodeOptions = {}) =>
      translateErrorCode(code, { ...opt, t }),
    [t],
  )
}
